import * as React from 'react';
import useI18n from 'src/hooks/useI18n';
import {useApi} from 'src/hooks/useApi';
import {FunnelChart} from '@spaced-out/ui-design-system/lib/components/Charts';
import {SubTitleMedium} from '@spaced-out/ui-design-system/lib/components/Text';
import {SemanticIcon} from '@spaced-out/ui-design-system/lib/components/Icon';
import {DateRangePickerDropdown} from 'src/components/lib/date-range-picker-dropdown/index';
import {
  getSubtractedDate,
  getTodayInTimezone,
  addTimezoneStartOfDay,
  addTimezoneEndOfDay,
} from '@spaced-out/ui-design-system/lib/utils';
import {selectAgencyData} from 'src/selectors/agency';
import {selectJobFilters} from 'src/selectors/analytics/analytics-hiring-pipeline';
import {useSelector} from 'react-redux';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {useHistory} from 'src/rerouter/index';
import {
  getCustomExportOptions,
  isFunnelChartDataEmpty,
} from 'src/utils/analytics-genesis';
import css from './hiring-pipeline-performance-chart.css';
import {EmptyChartComponent} from 'src/components/analytics-genesis/journeys-dashboard/common/EmptyChartComponent/EmptyChartComponent';


export function tooltipPointFormatter(): string {
  // @ts-expect-error - TS2683 - 'this' implicitly has type 'any' because it does not have a type annotation.
  const point = this;
  const formattedValue = point.formattedValue;
  return `
      <span id="pipeline-${formattedValue}" class="${css.tooltipPoint}">
          <span style="background-color:${point.color};" class="${css.indicatorCircle}"></span>
          <span class="${css.tooltipContent}"> ${point.series.name}: ${formattedValue}</span>
      </span>`;
}

export const HiringPipelinePerformanceChart = () => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const language = labelI18nInstance.language;
  const customExportOptions = React.useMemo(
    () => getCustomExportOptions(t),
    [language],
  );
  const router = useHistory();
  const {timezone: agencyTimezone} = useSelector(selectAgencyData);
  const selectedFilters = useSelector(selectJobFilters);

  const today = getTodayInTimezone(agencyTimezone);
  const startDate = getSubtractedDate(today, 29, 'd');
  const endDate = today;

  const [selectedDateRange, setSelectedDateRange] = React.useState({
    startDate,
    startDateUTC: addTimezoneStartOfDay(startDate, agencyTimezone),
    endDate,
    endDateUTC: addTimezoneEndOfDay(endDate, agencyTimezone),
  });

  const {job_id, job_department, job_location, job_source, job_recruiter} =
    selectedFilters;

  const {startDateUTC, endDateUTC} = selectedDateRange;

  const payload = React.useMemo(
    () => ({
      job_id,
      job_department,
      job_location,
      job_source,
      job_recruiter,
      end_date: endDateUTC,
      start_date: startDateUTC,
    }),
    [
      job_id,
      job_department,
      job_location,
      job_source,
      job_recruiter,
      startDateUTC,
      endDateUTC,
    ],
  );

  const {result, isLoading: isPlotDataLoading} = useApi(
    'analytics/crm/hiring-pipeline/pipeline-velocity/funnel',
    {
      method: 'POST',
      deps: [payload],
      data: {
        ...payload,
        funnel_type: 'milestone',
      },
    },
  );

  const series = result ?? [];
  const handleRedirectToAnalytics = () => {
    router.push('/reports/hiring-pipeline?fromAnalytics=true');
  };

  const seriesData = React.useMemo(() => {
    return [
      {
        name: 'Conversion Funnel',
        showInLegend: true,
        // @ts-expect-error - TS2339 - Property 'map' does not exist on type '{}'. | TS7031 - Binding element 'key' implicitly has an 'any' type. | TS7031 - Binding element 'value' implicitly has an 'any' type. | TS7031 - Binding element 'percentage' implicitly has an 'any' type.
        data: series.map(({key, value, percentage}) => {
          const formattedPercentage = Number(percentage).toFixed(2);
          return {
            name: key,
            y: value,
            showInLegend: true,
            formattedValue: `${key}<br>${value} (${formattedPercentage}%)`,
          };
        }),
      },
    ];
  }, [series]);

  const isChartDataEmpty = React.useMemo(
    () => isFunnelChartDataEmpty(seriesData),
    [seriesData],
  );

  const headerActions = () => (
    <div className={css.headerActions}>
      <DateRangePickerDropdown
        enableThisYear
        selectedDateRange={{...selectedDateRange, timezone: agencyTimezone}}
        // @ts-expect-error - TS2322 - Type 'Dispatch<SetStateAction<{ startDate: string; startDateUTC: string; endDate: string; endDateUTC: string; }>>' is not assignable to type '(dateRangeData: DateRangeWithTimezone) => void'.
        onRangeSelect={setSelectedDateRange}
        minDate={getSubtractedDate(today, 2, 'y')}
      />
      <Button
        type="ghost"
        iconLeftName="external-link"
        color="secondary"
        onClick={handleRedirectToAnalytics}
      />
    </div>
  );

  return (
    <FunnelChart
      cardTitle={
        <div className={css.chartTitle}>
          <SemanticIcon
            name="chart-simple"
            semantic="neutral"
            type="solid"
            size="small"
          />
          <SubTitleMedium>
            {t('Hiring_Pipeline_Performance', 'Hiring Pipeline Performance')}
          </SubTitleMedium>
        </div>
      }
      classNames={{
        wrapper: css.chartWrapper,
      }}
      isLoading={isPlotDataLoading}
      headerActions={headerActions()}
      legend={{
        enabled: false,
        shadow: false,
      }}
      tooltip={{
        pointFormatter: tooltipPointFormatter,
      }}
      series={seriesData}
      yAxis={{
        title: {
          text: '',
        },
      }}
      customExportOptions={customExportOptions}
      hasEmptyData={isChartDataEmpty}
      emptyText={<EmptyChartComponent />}
      plotOptions={{
        funnel: {
          dataLabels: {
            enabled: true,
            format: '{point.formattedValue}',
            useHTML: true,
          },
        },
      }}
    />
  );
};
